/**
 * Generic error boundary used to wrap pages so a single component crash
 * doesn't take the whole SPA down. Shows the error message + stack to
 * the user (we're a developer-facing tool, not a public-facing one) so
 * we can debug post-mortem.
 *
 * Usage:
 *   <ErrorBoundary scope="event-design-canvas">
 *     <EventDesignerCanvas {...props} />
 *   </ErrorBoundary>
 */
import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Short identifier used in the heading + commit-tag suggestion. */
  scope: string;
  /** Optional override of the default fallback. */
  fallback?: (err: Error, reset: () => void) => ReactNode;
}

interface State {
  err: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { err: null };

  static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  override componentDidCatch(err: Error, info: { componentStack?: string }): void {
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${this.props.scope}]`, err, info);
  }

  reset = (): void => this.setState({ err: null });

  override render(): ReactNode {
    const { err } = this.state;
    if (!err) return this.props.children;
    if (this.props.fallback) return this.props.fallback(err, this.reset);

    return (
      <div
        role="alert"
        data-testid={`error-boundary-${this.props.scope}`}
        style={{
          padding: '32px 28px',
          maxWidth: 720,
          margin: '40px auto',
          background: 'var(--bg-surface, #fff)',
          border: '1px solid var(--err, #b91c1c)',
          borderRadius: 8,
          fontFamily: 'var(--font-sans)',
          color: 'var(--text-1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 12,
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: 'var(--err, #b91c1c)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            !
          </span>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>
            {this.props.scope} crashed
          </h2>
        </div>
        <p style={{ fontSize: 13.5, color: 'var(--text-2)', marginBottom: 14 }}>
          A component below this boundary threw. The rest of Pipebase is
          unaffected — go back to the previous page or retry below.
        </p>
        <pre
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11.5,
            background: 'var(--bg-tint, #f5f5f4)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 6,
            padding: '10px 12px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: 'var(--text-2)',
            marginBottom: 14,
          }}
        >
          {err.name}: {err.message}
          {err.stack ? '\n' + err.stack.split('\n').slice(0, 6).join('\n') : ''}
        </pre>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={this.reset}
            className="btn btn--secondary"
            style={{ fontSize: 13 }}
          >
            Retry
          </button>
          <button
            type="button"
            onClick={() => {
              window.history.back();
            }}
            className="btn btn--secondary"
            style={{ fontSize: 13 }}
          >
            Go back
          </button>
        </div>
      </div>
    );
  }
}
